import React from 'react';

import { Autocomplete, Chip, TextField } from '@mui/material';

interface Props {
  label: string;
  values: Array<string>;
  onChange: (values: Array<string>) => void;
  required?: boolean;
  /** Only the URL field matches by prefix; UTM values are always exact. */
  supportsWildcards?: boolean;
}

// Operators paste campaign lists straight out of a spreadsheet, so accept the
// separators those exports use and turn each segment into its own tag.
const SEPARATORS = /[,|\n\t]/;

function splitPastedValue(text: string): Array<string> {
  return text
    .split(SEPARATORS)
    .map((value) => value.trim())
    .filter(Boolean);
}

export default function MarketingRouterMatchTagsField({ label, values, onChange, required, supportsWildcards }: Props) {
  const addValues = (incoming: Array<string>) => {
    const merged = [...values];

    for (const value of incoming) {
      if (value && !merged.includes(value)) {
        merged.push(value);
      }
    }

    onChange(merged);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      fullWidth
      options={[]}
      value={values}
      onChange={(_, nextValues) => onChange(nextValues.flatMap((value) => splitPastedValue(String(value))))}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((value, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={value} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required && values.length === 0}
          placeholder={
            values.length === 0
              ? supportsWildcards
                ? 'Type a URL, press Enter — end with * to match by prefix'
                : 'Type a value, press Enter — one tag per value'
              : ''
          }
          helperText={
            supportsWildcards ? 'Matched exactly, or by prefix when the value ends in *.' : 'Matched exactly.'
          }
          onPaste={(event) => {
            const text = event.clipboardData.getData('text');
            if (!SEPARATORS.test(text)) {
              return;
            }
            event.preventDefault();
            addValues(splitPastedValue(text));
          }}
        />
      )}
    />
  );
}
